import _ from "lodash";

import { isNativeApp } from "@/utils/env";
import { set } from "@/utils/vuex";
import { Storage } from "@/utils/storage";
import ability, { ForbiddenError } from "@/services/ability";

import api from "@/api/cloud";
import bridge from "@/api/bridge";

const storage = Storage.local();

const initBiometrics = {
	available: false,
	optedIn: false,
	stored: false,
};

const initState = () => ({
	user: null,
	biometrics: storage.get("biometrics", initBiometrics),
});

export default {
	namespaced: true,
	state: initState(),

	getters: {
		user: (state, getters) => {
			const user = Object.assign({}, state.user);
			user.roles = getters.roles;
			user.permissions = getters.permissions;
			return user;
		},

		roles: (state, _getters, _rootState, rootGetters) => {
			const roles = _.get(state, "user._roles", []);
			return _.chain(roles)
				.map(role => role._name)
				.compact()
				.uniq()
				.sort()
				.value();
		},

		hasRole: (state, getters) => name => {
			return _.includes(getters.roles, name);
		},

		permissions: (state, _getters, _rootState, rootGetters) => {
			return _.get(state, "user.scope", []);
		},


		firstName: (state) => _.get(state.user, "firstName"),

		lastName: (state) => _.get(state.user, "lastName"),

		email: (state) =>
			_.get(state.user, "email"),

		communities: (state) =>
			_.filter(state.user, '_communities'),

		id: (state) => {
			return state.user ? state.user._id : null;
		},

		fullName: (state, getters) =>
			[getters.firstName, getters.lastName].join(" "),

		signedIn: state => !!state.user,
	},

	mutations: {
		setUser: set("user"),

		setBiometrics: (state, payload) => {
			if (payload.optedIn === false) {
				// If user opts-out, then make sure we clear 'stored' flag as well.
				payload.stored = false;
			}
			state.biometrics = Object.assign({}, state.biometrics, payload);

			try {
				storage.set("biometrics", state.biometrics);
			} catch (e) {
				// Ignore possible "storage full" exception on iOS Safari.
				// Never want to throw an exception inside of a mutation.
			}
		},
	},

	actions: {
		// Automatically called once at store initialization
		async init({ dispatch }) {

			await api.init();
			// Check for biometrics (and wait on promise)
			return dispatch("checkBiometrics");
		},

		async checkBiometrics({ commit }) {
			let available = false;
			if (isNativeApp()) {
				try {
					available = await bridge.biometricIsAvailable();
					commit("setBiometrics", { available });
				} catch (e) {
					this._vm.$log(`Error checking for biometrics: ${e}`);
				}
			}
			return available;
		},

		async signIn({ dispatch }, { email, password }) {
			await api.jwtLogin(email, password);
			await api.ssoLogin(email, password);
			return dispatch("getCurrentUser");
		},

		async storeCredentials({ commit }, { email, password }) {
			// Value of `stored` seems to always be 'undefined'
			/* const stored = */ await bridge.biometricStore(email, password);
			commit("setBiometrics", { stored: true });
		},

		async signInBio({ dispatch }) {
			const canLogin = await bridge.biometricLogin();
			if (!canLogin) {
				this._vm.$log(`Biometric login: no credentials found`);
				return false;
			}

			this._vm.$log(`Biometric credentials retrieved`);
			const { username, password } = canLogin;
			return dispatch("signIn", { email: username, password });
		},

		async signOut({ commit }) {
			try {
				await api.logout();
			} catch (e) {
				// Eat exception. Failing to log out means you're already
				// logged out. Probably.
			}
			commit("setUser", null);
		},

		async getCurrentUser({ commit, dispatch, getters }) {
			const user = await api.getCurrentUser();
			const { _communityId, _operatorId } = user;
			
			commit("setUser", user);

			await dispatch("session/setCommunityId", {
				id: _communityId,
				operator: _operatorId
			}, { root: true });

			this._vm.$bugsnagClient.user = {
				id: getters.id,
				name: getters.fullName,
				email: getters.email,
			};

			// And load all roles and associated permissions
			await dispatch("roles/initRoles", null, { root: true });

			// Check authorization - user may not be allowed to use Ops Dasboard
			const canLogin = ability.can("access", "Ops");
			if (!canLogin) {
				throw new ForbiddenError(
					"User authenticated but lacks required permissions to access this app."
				);
			}

			// Once signed in, load all communities
			await dispatch("communities/load", null, { root: true });

			return user;
		},
	},
};
